import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useProjectBugs } from '@hooks/projects/useProjectBugs';
import { Form, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_PMO_SUBMIT_TO_CLIENT_ABILITY,
    PROJECT_PMO_RESPOND_ABILITY,
    PROJECT_PMO_CREATE_CHANGE_REQUEST_ABILITY,
    PROJECT_PMO_VIEW_CHANGE_REQUEST_ABILITY,
} from '@/ability';
import {
    getProjectPMOSubmissions,
    getProjectCompletionRequests,
    createCompletionRequest,
    respondToCompletionRequest,
    createProjectChangeRequest,
    getProjectChangeRequests,
    getProjectSprintTaskDetail,
} from '@services/projects.service';
import { useIsClientProfile } from '@hooks/useIsClientProfile';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import { buildFlatSprintsFromSubmissions } from './useProjectQASubmissions';
import {
    getTaskDescriptionMarkdown,
    getTaskDisplayId,
    getSprintTaskApiId,
    getMilestoneDisplayLabel,
    collectSprintsFromProjectMilestones,
    isLikelyFileAttachmentUrl,
    getTaskAttachmentListForModal,
} from './projectHelpers';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';

export const PROJECT_PMO_SUBMISSIONS_QUERY_KEY = (projectId, params) => [
    'projects',
    projectId,
    'pmo',
    'submissions',
    params,
];
export const PROJECT_COMPLETION_REQUESTS_QUERY_KEY = (projectId, params) => [
    'projects',
    projectId,
    'completion-request',
    params,
];
export const PROJECT_PMO_CHANGE_REQUESTS_QUERY_KEY = (projectId, params) => [
    'projects',
    projectId,
    'change-requests',
    params,
];

/** Submit PMO work to the client (modal submit, Kanban drag to “submitted to client”). */

/** Backend caps `limit` at 100 for GET /projects/:id/pmo/submissions */
const LIST_LIMIT = 100;
const PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY = (projectId, sprintId, taskId) => [
    'projects',
    projectId,
    'sprints',
    sprintId,
    'tasks',
    taskId,
    'detail',
];

export const COL_PMO = { SUBMISSION: 'submission', SUBMITTED_CLIENT: 'submitted-client' };

function removeLinkFromHtml(html, url) {
    if (!html || typeof html !== 'string' || !url) return html || '';
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const links = Array.from(doc.querySelectorAll('a[href]'));
        links.forEach((a) => {
            if (a.getAttribute('href') === url) a.remove();
        });
        return doc.body.innerHTML;
    } catch {
        // Fallback: remove anchor blocks containing href="url"
        const safe = String(url).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return String(html).replace(new RegExp(`<a[^>]*href="${safe}"[^>]*>[\\s\\S]*?<\\/a>`, 'g'), '');
    }
}

function removeAttachmentPlaceholderFromHtml(html, removedIndex) {
    if (!html || typeof html !== 'string') return html || '';
    if (removedIndex == null || Number.isNaN(Number(removedIndex))) return html;
    const idx = Number(removedIndex);
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const anchors = Array.from(doc.querySelectorAll('a[href^="#attachment-"]'));
        anchors.forEach((a) => {
            const href = a.getAttribute('href') || '';
            const m = href.match(/^#attachment-(\d+)$/);
            if (!m) return;
            const n = Number(m[1]);
            if (n === idx) {
                a.remove();
                return;
            }
            if (n > idx) {
                a.setAttribute('href', `#attachment-${n - 1}`);
            }
        });
        return doc.body.innerHTML;
    } catch {
        // Best-effort fallback: remove the exact placeholder anchor and shift subsequent href indices.
        let next = String(html);
        next = next.replace(new RegExp(`<a[^>]*href="#attachment-${idx}"[\\s\\S]*?<\\/a>`, 'g'), '');
        next = next.replace(/href="#attachment-(\d+)"/g, (m, nRaw) => {
            const n = Number(nRaw);
            if (Number.isNaN(n)) return m;
            if (n > idx) return `href="#attachment-${n - 1}"`;
            return m;
        });
        return next;
    }
}

function extractAttachmentPlaceholderCount(html) {
    if (!html || typeof html !== 'string') return 0;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const anchors = Array.from(doc.querySelectorAll('a[href^="#attachment-"]'));
        let max = -1;
        anchors.forEach((a) => {
            const href = a.getAttribute('href') || '';
            const m = href.match(/^#attachment-(\d+)$/);
            if (!m) return;
            const n = Number(m[1]);
            if (!Number.isNaN(n)) max = Math.max(max, n);
        });
        return max >= 0 ? max + 1 : 0;
    } catch {
        const matches = String(html).match(/href="#attachment-(\d+)"/g) || [];
        let max = -1;
        matches.forEach((m) => {
            const n = Number(String(m).match(/(\d+)/)?.[1]);
            if (!Number.isNaN(n)) max = Math.max(max, n);
        });
        return max >= 0 ? max + 1 : 0;
    }
}

function extractNonSpecialLinkHrefs(html) {
    if (!html || typeof html !== 'string') return new Set();
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const links = Array.from(doc.querySelectorAll('a[href]'));
        const set = new Set();
        links.forEach((a) => {
            const href = a.getAttribute('href') || '';
            if (!href) return;
            if (href.startsWith('#attachment-')) return;
            if (href.startsWith('#mention-user-')) return;
            if (href.startsWith('#mention-milestone-')) return;
            if (href.startsWith('#mention-sprint-')) return;
            if (href.startsWith('#')) return;
            set.add(href);
        });
        return set;
    } catch {
        const set = new Set();
        const re = /<a[^>]*href="([^"]+)"[^>]*>/g;
        let m;
        while ((m = re.exec(String(html)))) {
            const href = m[1] || '';
            if (!href || href.startsWith('#')) continue;
            if (href.startsWith('#attachment-')) continue;
            set.add(href);
        }
        return set;
    }
}

// Native HTML5 drag/drop removed (dnd-kit is used by PMO Kanban).

function normalizeCompletionRequestItem(item) {
    if (!item || typeof item !== 'object') return item;
    // New API returns completionRequestNotes/content/links/attachments alongside current status fields.
    // For consistent UI, treat completionRequestNotes as the "Request notes" source-of-truth.
    const requestNotes =
        item.completionRequestNotes ??
        item.completion_request_notes ??
        item.requestNotes ??
        item.request_notes ??
        item.notes ??
        (typeof item.completionRequestContent === 'string'
            ? { markdown: item.completionRequestContent }
            : typeof item.content === 'string'
                ? { markdown: item.content }
                : undefined);
    const normalizedRequestNotes =
        requestNotes && typeof requestNotes === 'object' && typeof requestNotes.markdown === 'string'
            ? (() => {
                const md = String(requestNotes.markdown || '');
                const looksHtml = /<[^>]+>/.test(md) || md.includes('&nbsp;');
                return looksHtml ? { ...requestNotes, markdown: htmlToMarkdown(md).trim() } : requestNotes;
            })()
            : requestNotes;
    const requestAttachmentsRaw =
        item.completionRequestAttachments ??
        item.completion_request_attachments ??
        item.requestAttachments ??
        item.request_attachments ??
        item.attachmentUrls ??
        [
            ...(Array.isArray(item.links) ? item.links : []),
            ...(Array.isArray(item.completionRequestLinks) ? item.completionRequestLinks : []),
        ];

    // Change-request response content comes in `notes` (often HTML-ish from RichTextEditor).
    // Normalize to markdown so the UI can render consistently with <Markdown />.
    const changeRequestNotesRaw = item.notes ?? item.changeRequestNotes ?? item.change_request_notes ?? null;
    const normalizedChangeRequestNotes =
        changeRequestNotesRaw && typeof changeRequestNotesRaw === 'object' && typeof changeRequestNotesRaw.markdown === 'string'
            ? (() => {
                const md = String(changeRequestNotesRaw.markdown || '');
                const looksHtml = /<[^>]+>/.test(md) || md.includes('&nbsp;');
                return looksHtml ? { ...changeRequestNotesRaw, markdown: htmlToMarkdown(md).trim() } : changeRequestNotesRaw;
            })()
            : typeof item.content === 'string' && item.content
                ? (() => {
                    const md = String(item.content || '');
                    const looksHtml = /<[^>]+>/.test(md) || md.includes('&nbsp;');
                    return { markdown: looksHtml ? htmlToMarkdown(md).trim() : md };
                })()
                : null;

    const changeRequestAttachmentsRaw = [
        ...(Array.isArray(item.links) ? item.links : []),
        ...(Array.isArray(item.attachments) ? item.attachments : []),
        ...(Array.isArray(changeRequestNotesRaw?.links) ? changeRequestNotesRaw.links : []),
    ];
    return {
        ...item,
        requestNotes: normalizedRequestNotes,
        requestAttachments: Array.isArray(requestAttachmentsRaw) ? requestAttachmentsRaw : [],
        changeRequestNotes: normalizedChangeRequestNotes,
        changeRequestAttachments: changeRequestAttachmentsRaw,
        milestone:
            item.milestone && typeof item.milestone === 'object'
                ? item.milestone
                : { id: item.milestoneId, name: item.milestoneName ?? '—' },
    };
}

export function isSubmissionPending(sub) {
    return String(sub?.status ?? '').toLowerCase() === 'pending';
}

/** Links / attachments from PMO submission API for read-only detail modal. */
function normalizeSubmissionLinkItems(items) {
    if (!Array.isArray(items)) return [];
    return items
        .map((x) => {
            if (!x) return null;
            if (typeof x === 'string') return { url: x, title: x };
            const url = x.url ?? x.href ?? '';
            if (!url) return null;
            return { url: String(url), title: String(x.title ?? x.name ?? url) };
        })
        .filter(Boolean);
}

/**
 * Normalize API attachments/links into the same `{ type, url, title }[]` shape that milestone
 * description attachments use, so we can reuse the same dropdown UI.
 */
function normalizeSubmissionAttachmentItems(items) {
    if (!Array.isArray(items)) return [];
    return items
        .map((x) => {
            if (!x) return null;
            if (typeof x === 'string') {
                const url = String(x);
                const type = isLikelyFileAttachmentUrl(url) ? 'file' : 'link';
                return { type, url, title: url };
            }
            const url = x.url ?? x.href ?? '';
            if (!url) return null;
            const rawType = String(x.type ?? '').toLowerCase();
            const inferredType = isLikelyFileAttachmentUrl(url) ? 'file' : 'link';
            const type = rawType === 'file' || rawType === 'link' ? rawType : inferredType;
            return { type, url: String(url), title: String(x.title ?? x.name ?? url) };
        })
        .filter(Boolean);
}

/** Attachments-only normalization: excludes link-type values from `attachments`. */
function normalizeSubmissionAttachmentOnlyItems(items) {
    if (!Array.isArray(items)) return [];
    return items
        .map((x) => {
            if (!x) return null;
            if (typeof x === 'string') {
                const url = String(x);
                if (!isLikelyFileAttachmentUrl(url)) return null;
                return { type: 'file', url, title: url };
            }
            const url = x.url ?? x.href ?? '';
            if (!url) return null;
            const rawType = String(x.type ?? '').toLowerCase();
            if (rawType === 'link') return null;
            if (!isLikelyFileAttachmentUrl(url) && rawType !== 'file') return null;
            return { type: 'file', url: String(url), title: String(x.title ?? x.name ?? url) };
        })
        .filter(Boolean);
}

function mergeSubmissionAttachmentSources(links, attachments) {
    return [
        ...normalizeSubmissionAttachmentItems(Array.isArray(links) ? links : []),
        ...normalizeSubmissionAttachmentOnlyItems(Array.isArray(attachments) ? attachments : []),
    ];
}

function normalizeSubmissionNotesMarkdown(raw) {
    const m = raw?.markdown ?? raw?.content ?? raw?.note ?? raw;
    return typeof m === 'string' ? m : '';
}

function buildSubmissionDetailSections(detailSubmission) {
    if (!detailSubmission) return [];
    const out = [];

    if (detailSubmission.qaSubmission) {
        const qa = detailSubmission.qaSubmission;
        out.push({
            key: 'qa',
            label: 'QA submission',
            submittedAt: qa.submittedAt ?? null,
            submittedBy: qa.submittedBy ?? null,
            contentMarkdown: normalizeSubmissionNotesMarkdown(qa.notes) || normalizeSubmissionNotesMarkdown(qa.content),
            attachmentItems: mergeSubmissionAttachmentSources(qa.links, qa.attachments),
        });
    }

    if (detailSubmission.developerSubmission) {
        const dev = detailSubmission.developerSubmission;
        out.push({
            key: 'developer',
            label: 'Developer submission',
            submittedAt: dev.submittedAt ?? null,
            submittedBy: dev.submittedBy ?? null,
            contentMarkdown: normalizeSubmissionNotesMarkdown(dev.notes) || normalizeSubmissionNotesMarkdown(dev.content),
            attachmentItems: mergeSubmissionAttachmentSources(dev.links, dev.attachments),
        });
    }

    if (detailSubmission.pmoSubmission) {
        const pmo = detailSubmission.pmoSubmission;
        out.push({
            key: 'pmo',
            label: 'PMO submission',
            submittedAt: pmo.submittedAt ?? null,
            submittedBy: pmo.submittedBy ?? null,
            contentMarkdown: normalizeSubmissionNotesMarkdown(pmo.notes) || normalizeSubmissionNotesMarkdown(pmo.content),
            attachmentItems: mergeSubmissionAttachmentSources(pmo.links, pmo.attachments),
        });
    }

    if (out.length === 0) {
        const raw = detailSubmission?.notes?.markdown ?? detailSubmission?.content ?? '';
        const contentMarkdown = typeof raw === 'string' ? raw : '';
        out.push({
            key: 'submission',
            label: 'Submission notes',
            submittedAt: detailSubmission?.submittedAt ?? null,
            submittedBy: detailSubmission?.submittedBy ?? null,
            contentMarkdown,
            attachmentItems: mergeSubmissionAttachmentSources(detailSubmission?.links, detailSubmission?.attachments),
        });
    }

    return out;
}

function findSubmissionForTask(submissions, sprintId, taskId) {
    if (sprintId != null && taskId != null && String(sprintId) === String(taskId)) {
        const direct = submissions.find((s) => String(s.id) === String(sprintId));
        if (direct) return direct;
    }
    for (const sub of submissions) {
        const flat = buildFlatSprintsFromSubmissions([sub]);
        for (const sp of flat) {
            if (String(sp.id) !== String(sprintId)) continue;
            for (const t of sp.tasks || []) {
                const tid = t.taskId ?? t.id;
                if (String(tid) === String(taskId)) return sub;
            }
        }
    }
    return null;
}

/**
 * PMO list API often returns submissions without embedded `sprints` / `tasks`.
 * Build a minimal sprint+task shape so Kanban and SprintTaskCard can render.
 */
function buildSyntheticSprintAndTask(sub) {
    const milestoneName = getMilestoneDisplayLabel(sub?.milestone);
    const summary =
        (typeof sub?.notes?.markdown === 'string' && sub.notes.markdown.trim()) ||
        (typeof sub?.content === 'string' ? sub.content.trim() : '') ||
        '';
    const title = summary
        ? `${milestoneName} · ${summary.slice(0, 48)}${summary.length > 48 ? '…' : ''}`
        : `${milestoneName} · PMO submission`;
    const syntheticTask = {
        id: sub.id,
        taskId: sub.id,
        title,
        name: title,
        description: sub.notes?.markdown != null ? { markdown: sub.notes.markdown } : sub.content,
        activity_logs: [],
    };
    const syntheticSprint = {
        id: sub.id,
        name: '—',
        milestone: sub.milestone,
        milestoneId: sub.milestone?.id,
    };
    return { sprint: syntheticSprint, task: syntheticTask };
}

function getPmoTaskPreviewLine(task) {
    if (!task) return '—';
    const title = (task.title ?? task.name ?? '').trim();
    const d = task.description;
    let raw = '';
    if (typeof d === 'string') raw = d;
    else if (d && typeof d.markdown === 'string') raw = d.markdown;
    const stripped = String(raw)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const line = stripped || title || '—';
    if (line.length > 72) return `${line.slice(0, 72)}…`;
    return line;
}

function normalizeTaskAttachmentItemsForCard(task) {
    const list = getTaskAttachmentListForModal(task);
    if (!Array.isArray(list) || list.length === 0) return [];
    return list
        .map((x) => {
            const url = x?.rawUrl;
            if (!url) return null;
            const kind = String(x?.kind ?? '').toLowerCase();
            const type = kind === 'link' ? 'link' : 'file';
            const title = x?.name ?? url;
            return { type, url, title };
        })
        .filter(Boolean);
}

function extractSubmissionNotesPreview(sub) {
    const candidates = [
        sub?.qaSubmission?.notes,
        sub?.qaSubmission?.content,
        sub?.pmoSubmission?.notes,
        sub?.pmoSubmission?.content,
        sub?.notes?.markdown,
        sub?.content,
    ];
    for (const c of candidates) {
        const raw = normalizeSubmissionNotesMarkdown(c);
        const text = String(raw || '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (text) return text.length > 120 ? `${text.slice(0, 120)}…` : text;
    }
    return '';
}

function buildSubmissionCardSections(sub) {
    if (!sub) return [];
    const sections = [];

    const mkPreview = (raw) => {
        const s = normalizeSubmissionNotesMarkdown(raw);
        return typeof s === 'string' ? s.trim() : '';
    };

    if (sub.qaSubmission) {
        const qa = sub.qaSubmission;
        const attachmentItems = mergeSubmissionAttachmentSources(qa.links, qa.attachments);
        const previewRaw = mkPreview(qa.notes) || mkPreview(qa.content);
        const previewMarkdown = previewRaw;
        sections.push({
            key: 'qa',
            label: 'QA submission',
            previewMarkdown,
            attachmentItems,
        });
    }

    if (sub.developerSubmission) {
        const dev = sub.developerSubmission;
        const attachmentItems = mergeSubmissionAttachmentSources(dev.links, dev.attachments);
        const previewRaw = mkPreview(dev.notes) || mkPreview(dev.content);
        const previewMarkdown = previewRaw;
        sections.push({
            key: 'developer',
            label: 'Developer submission',
            previewMarkdown,
            attachmentItems,
        });
    }

    if (sub.pmoSubmission) {
        const pmo = sub.pmoSubmission;
        const attachmentItems = mergeSubmissionAttachmentSources(pmo.links, pmo.attachments);
        const previewRaw = mkPreview(pmo.notes) || mkPreview(pmo.content);
        const previewMarkdown = previewRaw;
        sections.push({
            key: 'pmo',
            label: 'PMO submission',
            previewMarkdown,
            attachmentItems,
        });
    }

    if (sections.length === 0) {
        const attachmentItems = mergeSubmissionAttachmentSources(sub?.links, sub?.attachments);
        const previewRaw = mkPreview(sub?.notes?.markdown) || mkPreview(sub?.content);
        const previewMarkdown = previewRaw;
        if (previewMarkdown || attachmentItems.length > 0) {
            sections.push({
                key: 'submission',
                label: 'Submission',
                previewMarkdown,
                attachmentItems,
            });
        }
    }

    return sections;
}

/**
 * One kanban card per submission: header (SID + title) + nested tasks. Mirrors PMO API shape.
 */
function buildPartitionedSubmissionRows(submissionsOrdered) {
    const submissionColumn = [];
    const submittedToClientColumn = [];
    submissionsOrdered.forEach((sub, ord) => {
        const flat = buildFlatSprintsFromSubmissions([sub]);
        const pending = isSubmissionPending(sub);
        const taskEntries = [];
        flat.forEach((sp) => {
            (sp.tasks || []).forEach((task) => {
                taskEntries.push({
                    key: `${sub.id}-${task.taskId ?? task.id}`,
                    sprint: sp,
                    task,
                    isSynthetic: false,
                    previewLine: getPmoTaskPreviewLine(task),
                    attachmentItems: normalizeTaskAttachmentItemsForCard(task),
                });
            });
        });
        if (taskEntries.length === 0) {
            const { sprint, task } = buildSyntheticSprintAndTask(sub);
            taskEntries.push({
                key: `pmo-sub-${sub.id}`,
                sprint,
                task,
                isSynthetic: true,
                previewLine: getPmoTaskPreviewLine(task),
                attachmentItems: normalizeTaskAttachmentItemsForCard(task),
            });
        }
        const n = ord + 1;
        const milestoneLabel = getMilestoneDisplayLabel(sub?.milestone);
        const cardSections = buildSubmissionCardSections(sub);
        const row = {
            key: sub.id,
            submission: sub,
            taskEntries,
            sidLabel: `SID-${n}`,
            submissionTitle: milestoneLabel,
            hasSyntheticOnly: taskEntries.length === 1 && taskEntries[0].isSynthetic,
            notesPreview: extractSubmissionNotesPreview(sub),
            cardSections,
        };
        if (pending) submissionColumn.push(row);
        else submittedToClientColumn.push(row);
    });
    return { submissionColumn, submittedToClientColumn };
}

/**
 * PMO tab: submissions API, kanban columns, submit-to-client modal, client feedback, task detail.
 */
export function useProjectPMO(projectId, _project, options = {}) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const isClient = useIsClientProfile();
    const { enabled = true } = options;

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId && enabled });
    const mentionUsers = useMemo(() => (project?.assignedUsers ?? []).slice(0, 100), [project?.assignedUsers]);
    const mentionMilestones = useMemo(() => (project?.milestones ?? []), [project?.milestones]);
    const mentionSprints = useMemo(() => collectSprintsFromProjectMilestones(project), [project]);

    const canSubmitToClient = can(PROJECT_PMO_SUBMIT_TO_CLIENT_ABILITY);
    const canRespondToRequest = can(PROJECT_PMO_RESPOND_ABILITY);
    const canCreateChangeRequest = can(PROJECT_PMO_CREATE_CHANGE_REQUEST_ABILITY);
    const canViewChangeRequests = can(PROJECT_PMO_VIEW_CHANGE_REQUEST_ABILITY);

    const { bugs: projectBugs = [] } = useProjectBugs(projectId, {
        enabled: !!projectId && enabled && !isClient,
    });

    const [pmoViewTab, setPmoViewTab] = useState('submission');

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [submitModalOpen, setSubmitModalOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [submissionDetailModalOpen, setSubmissionDetailModalOpen] = useState(false);
    const [detailSubmission, setDetailSubmission] = useState(null);
    const [notes, setNotes] = useState('');
    const [pendingLinks, setPendingLinks] = useState([]);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const linkModalCallbackRef = useRef(null);
    const [linkForm] = Form.useForm();

    const [respondModalOpen, setRespondModalOpen] = useState(false);
    const [selectedRespondRequestId, setSelectedRespondRequestId] = useState(null);
    const [respondAction, setRespondAction] = useState('accepted');
    const [respondResponseTab, setRespondResponseTab] = useState('backlog'); // declined only: backlog | change_request
    const [respondNote, setRespondNote] = useState('');
    const [respondRating, setRespondRating] = useState(5);
    const [respondPendingLinks, setRespondPendingLinks] = useState([]);
    const [respondPendingFiles, setRespondPendingFiles] = useState([]);
    const [respondLinkModalOpen, setRespondLinkModalOpen] = useState(false);
    const respondLinkModalCallbackRef = useRef(null);
    const [respondLinkForm] = Form.useForm();

    const [changeRequestForm] = Form.useForm();
    const [selectedChangeRequestSource, setSelectedChangeRequestSource] = useState(null);
    const [changeRequestPendingLinks, setChangeRequestPendingLinks] = useState([]);
    const [changeRequestPendingFiles, setChangeRequestPendingFiles] = useState([]);
    const [changeRequestLinkModalOpen, setChangeRequestLinkModalOpen] = useState(false);
    const changeRequestLinkModalCallbackRef = useRef(null);
    const [changeRequestLinkForm] = Form.useForm();

    // Client request detail (view-only) uses the same submission detail modal chrome.
    const [clientRequestDetailOpen, setClientRequestDetailOpen] = useState(false);
    const [clientRequestDetail, setClientRequestDetail] = useState(null);

    const changeRequestDescriptionHtml = Form.useWatch('description', changeRequestForm);

    const isPMOEnabled = project?.capabilities?.submissionMode && project?.capabilities?.submissionMode !== 'none';

    const listParams = useMemo(() => ({ page: 1, limit: LIST_LIMIT }), []);
    const pmoQuery = useQuery({
        queryKey: PROJECT_PMO_SUBMISSIONS_QUERY_KEY(projectId, listParams),
        queryFn: () => getProjectPMOSubmissions(projectId, listParams),
        enabled: !!projectId && enabled && !isClient && !!isPMOEnabled,
    });

    const feedbackParams = useMemo(() => ({ page, limit }), [page, limit]);
    const completionRequestQuery = useQuery({
        queryKey: PROJECT_COMPLETION_REQUESTS_QUERY_KEY(projectId, feedbackParams),
        queryFn: () => getProjectCompletionRequests(projectId, feedbackParams),
        enabled: !!projectId && enabled && !!isPMOEnabled,
    });

    const changeRequestsParams = useMemo(() => ({ page: 1, limit: 20 }), []);
    const changeRequestsQuery = useQuery({
        queryKey: PROJECT_PMO_CHANGE_REQUESTS_QUERY_KEY(projectId, changeRequestsParams),
        queryFn: () => getProjectChangeRequests(projectId, changeRequestsParams),
        enabled: !!projectId && enabled && isClient && canViewChangeRequests && !!isPMOEnabled,
    });

    const { data: pmoData, isLoading: pmoLoading, isError: pmoError, refetch: refetchPmo } = pmoQuery;
    const {
        data: completionData,
        isLoading: completionLoading,
        refetch: refetchCompletion,
    } = completionRequestQuery;
    const { data: changeRequestsData, isLoading: changeRequestsLoading } = changeRequestsQuery;

    const submitToClientMutation = useMutation({
        mutationFn: (payload) =>
            createCompletionRequest(projectId, {
                submissionId: payload.submissionId,
                notes:
                    payload?.notes?.markdown != null && String(payload.notes.markdown).trim() !== ''
                        ? { markdown: payload.notes.markdown }
                        : undefined,
                attachmentUrls: payload.attachmentUrls,
                files: payload.files,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'pmo', 'submissions'] });
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'completion-request'] });
        },
    });

    const respondMutation = useMutation({
        mutationFn: ({ requestId, action, note, rating, attachmentUrls, files }) =>
            respondToCompletionRequest(projectId, requestId, {
                action,
                note: note || undefined,
                rating,
                attachmentUrls,
                files,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'completion-request'] });
        },
    });

    const createChangeRequestMutation = useMutation({
        mutationFn: (payload) => createProjectChangeRequest(projectId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'change-requests'] });
            queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'completion-request'] });
        },
    });

    const rawPmoList = Array.isArray(pmoData?.data) ? pmoData.data : [];
    const submissions = useMemo(() => rawPmoList, [rawPmoList]);

    const rawClientList = Array.isArray(completionData?.data) ? completionData.data : [];
    const clientSubmissions = useMemo(
        () => rawClientList.map(normalizeCompletionRequestItem),
        [rawClientList],
    );

    const changeRequests = useMemo(() => {
        const list = Array.isArray(changeRequestsData?.data) ? changeRequestsData.data : [];
        return list;
    }, [changeRequestsData]);

    const clientFeedbackItems = useMemo(() => {
        const approvedRejected = clientSubmissions.filter(
            (x) => String(x?.status ?? '').toLowerCase() !== 'pending',
        );
        const orderedCompletion = [...approvedRejected].sort(
            (a, b) => new Date(b?.updatedAt ?? b?.respondedAt ?? b?.createdAt ?? 0) - new Date(a?.updatedAt ?? a?.respondedAt ?? a?.createdAt ?? 0),
        );
        const orderedChangeRequests = [...changeRequests].sort(
            (a, b) => new Date(b?.createdAt ?? b?.updatedAt ?? 0) - new Date(a?.createdAt ?? a?.updatedAt ?? 0),
        );
        return { completionResponses: orderedCompletion, changeRequests: orderedChangeRequests };
    }, [clientSubmissions, changeRequests]);

    const openClientRequestDetail = useCallback((req) => {
        if (!req) return;
        setClientRequestDetail(req);
        setClientRequestDetailOpen(true);
    }, []);

    const closeClientRequestDetail = useCallback(() => {
        setClientRequestDetailOpen(false);
        setClientRequestDetail(null);
    }, []);

    const clientRequestDetailPresentation = useMemo(() => {
        if (!clientRequestDetail) return { sidLabel: 'CR-1', presetHeading: 'Completion request' };
        const idx = clientSubmissions.findIndex((x) => String(x?.id ?? '') === String(clientRequestDetail?.id ?? ''));
        const n = idx >= 0 ? idx + 1 : 1;
        const heading = getMilestoneDisplayLabel(clientRequestDetail?.milestone) || 'Completion request';
        return { sidLabel: `CR-${n}`, presetHeading: heading };
    }, [clientRequestDetail, clientSubmissions]);

    const clientRequestDetailNotesRaw = useMemo(() => {
        const raw = clientRequestDetail?.requestNotes?.markdown ?? '';
        return typeof raw === 'string' ? raw : '';
    }, [clientRequestDetail]);

    const clientRequestDetailViewAttachments = useMemo(
        () => {
            const status = String(clientRequestDetail?.status ?? '').toLowerCase();
            if (status === 'change-request') {
                return normalizeSubmissionLinkItems(
                    Array.isArray(clientRequestDetail?.changeRequestAttachments)
                        ? clientRequestDetail.changeRequestAttachments
                        : clientRequestDetail?.attachments,
                );
            }
            return normalizeSubmissionLinkItems(clientRequestDetail?.requestAttachments);
        },
        [clientRequestDetail],
    );

    const clientRequestDetailViewClientAttachments = useMemo(
        () => {
            const status = String(clientRequestDetail?.status ?? '').toLowerCase();
            if (status === 'change-request') return normalizeSubmissionLinkItems([]);
            return normalizeSubmissionLinkItems(clientRequestDetail?.clientAttachments);
        },
        [clientRequestDetail],
    );

    const clientRequestDetailSections = useMemo(() => {
        if (!clientRequestDetail) return [];
        const status = String(clientRequestDetail?.status ?? '').toLowerCase();
        const isChangeRequest = status === 'change-request';

        const requestedAt = clientRequestDetail?.requestedAt ?? clientRequestDetail?.createdAt ?? null;
        const requestedBy = clientRequestDetail?.requestedByName ?? clientRequestDetail?.requestedBy ?? null;

        const out = [];
        if (isChangeRequest) {
            // In change-request status, backend can send BOTH:
            // 1) original completion-request notes (requestNotes / completionRequestNotes)
            // 2) change-request notes (notes)
            const requestNotesMd = normalizeSubmissionNotesMarkdown(clientRequestDetail?.requestNotes) || '';
            const requestAttachmentItems = normalizeSubmissionAttachmentItems(
                Array.isArray(clientRequestDetail?.requestAttachments) ? clientRequestDetail.requestAttachments : [],
            );
            out.push({
                key: 'request',
                label: 'Request notes',
                submittedAt: requestedAt,
                submittedBy: requestedBy,
                contentMarkdown: requestNotesMd,
                attachmentItems: requestAttachmentItems,
            });

            const changeNotesMd = normalizeSubmissionNotesMarkdown(
                clientRequestDetail?.changeRequestNotes ?? clientRequestDetail?.notes ?? clientRequestDetail?.content,
            );
            const changeAttachmentItems = normalizeSubmissionAttachmentItems(
                Array.isArray(clientRequestDetail?.changeRequestAttachments)
                    ? clientRequestDetail.changeRequestAttachments
                    : Array.isArray(clientRequestDetail?.attachments)
                        ? clientRequestDetail.attachments
                        : Array.isArray(clientRequestDetail?.links)
                            ? clientRequestDetail.links
                            : [],
            );
            out.push({
                key: 'change-request',
                label: 'Change request notes',
                submittedAt: requestedAt,
                submittedBy: requestedBy,
                contentMarkdown: changeNotesMd || '',
                attachmentItems: changeAttachmentItems,
            });
            return out;
        }

        const requestNotesMd = normalizeSubmissionNotesMarkdown(clientRequestDetail?.requestNotes) || '';
        const requestAttachmentItems = normalizeSubmissionAttachmentItems(
            Array.isArray(clientRequestDetail?.requestAttachments) ? clientRequestDetail.requestAttachments : [],
        );

        const respondedAt = clientRequestDetail?.respondedAt ?? clientRequestDetail?.updatedAt ?? null;
        const clientNotesMd =
            normalizeSubmissionNotesMarkdown(clientRequestDetail?.clientNote ?? clientRequestDetail?.note) || '';
        const clientAttachmentItems = normalizeSubmissionAttachmentItems(
            Array.isArray(clientRequestDetail?.clientAttachments) ? clientRequestDetail.clientAttachments : [],
        );

        out.push({
            key: 'request',
            label: 'Request notes',
            submittedAt: requestedAt,
            submittedBy: requestedBy,
            contentMarkdown: requestNotesMd,
            attachmentItems: requestAttachmentItems,
        });
        if (clientNotesMd || clientAttachmentItems.length > 0) {
            out.push({
                key: 'response',
                label: 'Your response',
                submittedAt: respondedAt,
                submittedBy: null,
                contentMarkdown: clientNotesMd,
                attachmentItems: clientAttachmentItems,
            });
        }
        return out;
    }, [clientRequestDetail]);

    const sortedPmoSubmissions = useMemo(() => {
        const list = [...(submissions || [])];
        list.sort((a, b) => new Date(b?.submittedAt || 0) - new Date(a?.submittedAt || 0));
        return list;
    }, [submissions]);

    const { submissionColumn, submittedToClientColumn } = useMemo(
        () => buildPartitionedSubmissionRows(sortedPmoSubmissions),
        [sortedPmoSubmissions],
    );

    const submitModalPresentation = useMemo(() => {
        if (!selectedSubmission) {
            return { sidLabel: 'SID-1', presetHeading: 'Submission 1' };
        }
        const idx = sortedPmoSubmissions.findIndex((s) => String(s.id) === String(selectedSubmission.id));
        const n = idx >= 0 ? idx + 1 : 1;
        return { sidLabel: `SID-${n}`, presetHeading: `Submission ${n}` };
    }, [selectedSubmission, sortedPmoSubmissions]);

    /** Submission description from API — shown read-only as plain text in submit-to-client modal (below blank note editor). */
    const submitModalSubmissionDescriptionRaw = useMemo(() => {
        if (!selectedSubmission) return '';
        const raw = selectedSubmission?.notes?.markdown ?? selectedSubmission?.content ?? '';
        return typeof raw === 'string' ? raw : '';
    }, [selectedSubmission]);

    const submitModalDetailSections = useMemo(
        () => buildSubmissionDetailSections(selectedSubmission),
        [selectedSubmission],
    );

    const buildPmoModalTaskRows = useCallback((sub) => {
        if (!sub) return [];
        const flat = buildFlatSprintsFromSubmissions([sub]);
        const rows = [];
        let i = 0;
        flat.forEach((sp) => {
            (sp.tasks || []).forEach((t) => {
                rows.push({
                    key: `${sub.id}-${t.taskId ?? t.id}-${i}`,
                    taskIdLabel: getTaskDisplayId(t),
                    title: t.title ?? t.name ?? '—',
                    descriptionMarkdown: getTaskDescriptionMarkdown(t) || '',
                    attachmentItems: normalizeTaskAttachmentItemsForCard(t),
                    removeType: 'task',
                    removeKey: t.taskId ?? t.id,
                    payload: { sprint: sp, task: t },
                });
                i += 1;
            });
        });
        if (rows.length === 0) {
            const { sprint, task } = buildSyntheticSprintAndTask(sub);
            rows.push({
                key: `${sub.id}-summary`,
                taskIdLabel: 'Submission',
                title: task.title ?? task.name ?? '—',
                descriptionMarkdown: getTaskDescriptionMarkdown(task) || '',
                attachmentItems: normalizeTaskAttachmentItemsForCard(task),
                removeType: 'task',
                removeKey: task.taskId,
                payload: { sprint, task },
            });
        }
        return rows;
    }, []);

    const pmoSubmitModalTaskRows = useMemo(
        () => buildPmoModalTaskRows(selectedSubmission),
        [buildPmoModalTaskRows, selectedSubmission],
    );

    const submissionDetailPresentation = useMemo(() => {
        if (!detailSubmission) {
            return { sidLabel: 'SID-1', presetHeading: 'Submission 1' };
        }
        const idx = sortedPmoSubmissions.findIndex((s) => String(s.id) === String(detailSubmission.id));
        const n = idx >= 0 ? idx + 1 : 1;
        const milestoneLabel = getMilestoneDisplayLabel(detailSubmission?.milestone) || `Submission ${n}`;
        return { sidLabel: `SID-${n}`, presetHeading: milestoneLabel };
    }, [detailSubmission, sortedPmoSubmissions]);

    const submissionDetailDescriptionRaw = useMemo(() => {
        if (!detailSubmission) return '';
        const raw = detailSubmission?.notes?.markdown ?? detailSubmission?.content ?? '';
        return typeof raw === 'string' ? raw : '';
    }, [detailSubmission]);

    const pmoSubmissionDetailTaskRows = useMemo(
        () => buildPmoModalTaskRows(detailSubmission),
        [buildPmoModalTaskRows, detailSubmission],
    );

    const submissionDetailViewLinks = useMemo(
        () => normalizeSubmissionLinkItems(detailSubmission?.links),
        [detailSubmission],
    );

    const submissionDetailViewAttachments = useMemo(
        () => normalizeSubmissionAttachmentOnlyItems(detailSubmission?.attachments),
        [detailSubmission],
    );

    const submissionDetailViewClientAttachments = useMemo(
        () => normalizeSubmissionLinkItems(detailSubmission?.clientAttachments),
        [detailSubmission],
    );

    const submissionDetailSections = useMemo(
        () => buildSubmissionDetailSections(detailSubmission),
        [detailSubmission],
    );

    const handlePageChange = useCallback((newPage, newPageSize) => {
        setPage(newPage);
        if (newPageSize && newPageSize !== limit) {
            setLimit(newPageSize);
        }
    }, [limit]);

    const submitToClient = useCallback(
        (payload, callbacks = {}) => {
            submitToClientMutation.mutate(payload, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [submitToClientMutation],
    );

    const openSubmitModal = useCallback((sub) => {
        setSubmissionDetailModalOpen(false);
        setDetailSubmission(null);
        setSelectedSubmission(sub);
        /** Client-facing note is typed fresh in the modal — do not pre-fill from submission/API text. */
        setNotes('');
        setPendingLinks([]);
        setPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setSubmitModalOpen(true);
    }, []);

    const closeSubmitModal = useCallback(() => {
        setSubmitModalOpen(false);
        setSelectedSubmission(null);
        setNotes('');
        setPendingLinks([]);
        setPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
    }, []);

    const openSubmissionDetailModal = useCallback((sub) => {
        if (!sub) return;
        setSubmitModalOpen(false);
        setSelectedSubmission(null);
        setNotes('');
        setPendingLinks([]);
        setPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setDetailSubmission(sub);
        setSubmissionDetailModalOpen(true);
    }, []);

    const closeSubmissionDetailModal = useCallback(() => {
        setSubmissionDetailModalOpen(false);
        setDetailSubmission(null);
    }, []);

    const handleFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImage = (file.type || '').startsWith('image/');
            return {
                id: `submit-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImage ? URL.createObjectURL(file) : null,
            };
        });
        setPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const handleRemoveAttachment = useCallback((id) => {
        setPendingFiles((prev) => {
            const match = prev.find((p) => p.id === id);
            if (match?.preview) {
                try {
                    URL.revokeObjectURL(match.preview);
                } catch {
                    // ignore
                }
            }
            return prev.filter((p) => p.id !== id);
        });
    }, []);

    const handleRemoveLink = useCallback((id) => {
        setPendingLinks((prev) => prev.filter((l) => l.id !== id));
    }, []);

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkForm],
    );

    const closeLinkModal = useCallback(() => {
        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
    }, []);

    const handleLinkModalSubmit = useCallback(() => {
        linkForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) return;
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setPendingLinks((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkForm]);

    const handleSubmitToClient = useCallback(
        (callbacks = {}) => {
            if (!selectedSubmission?.id) return;
            let notesMarkdown;
            const raw = (notes || '').trim();
            if (raw) {
                try {
                    notesMarkdown = raw.startsWith('<') ? htmlToMarkdown(raw).trim() : raw;
                } catch {
                    notesMarkdown = raw;
                }
            }
            if (notesMarkdown === '') notesMarkdown = undefined;
            const notesObj = notesMarkdown ? { markdown: notesMarkdown } : undefined;
            const attachmentUrls =
                pendingLinks.length > 0
                    ? pendingLinks.map((l) => ({ type: 'link', url: l.url, title: l.title }))
                    : undefined;
            const files = pendingFiles.length > 0 ? pendingFiles.map((p) => p.file) : undefined;
            submitToClient(
                {
                    submissionId: selectedSubmission.id,
                    notes: notesObj,
                    attachmentUrls,
                    files,
                },
                {
                    onSuccess: () => {
                        closeSubmitModal();
                        callbacks.onSuccess?.();
                    },
                    onError: callbacks.onError,
                },
            );
        },
        [selectedSubmission, notes, pendingLinks, pendingFiles, submitToClient, closeSubmitModal],
    );

    const pendingSubmitToClientId =
        submitToClientMutation.isPending ? (submitToClientMutation.variables?.submissionId ?? null) : null;

    /**
     * Keep pending link/file chips in sync with the editor HTML even if user deletes directly in the editor
     * (select + delete, cut, etc). This mirrors Create Task behavior.
     */
    useEffect(() => {
        if (!respondModalOpen) return;
        const hrefs = extractNonSpecialLinkHrefs(respondNote || '');
        if (hrefs.size > 0) {
            setRespondPendingLinks((prev) => prev.filter((l) => !l?.url || hrefs.has(l.url)));
        } else {
            setRespondPendingLinks((prev) => (prev.length ? [] : prev));
        }
        const needed = extractAttachmentPlaceholderCount(respondNote || '');
        setRespondPendingFiles((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            if (needed >= prev.length) return prev;
            const toRemove = prev.slice(needed);
            toRemove.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return prev.slice(0, needed);
        });
    }, [respondNote, respondModalOpen]);

    useEffect(() => {
        // Change request drafting is hosted inside the response modal (declined → "Change request" tab).
        if (!respondModalOpen || respondResponseTab !== 'change_request') return;
        const html = changeRequestDescriptionHtml || '';
        const hrefs = extractNonSpecialLinkHrefs(html);
        if (hrefs.size > 0) {
            setChangeRequestPendingLinks((prev) => prev.filter((l) => !l?.url || hrefs.has(l.url)));
        } else {
            setChangeRequestPendingLinks((prev) => (prev.length ? [] : prev));
        }
        const needed = extractAttachmentPlaceholderCount(html);
        setChangeRequestPendingFiles((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            if (needed >= prev.length) return prev;
            const toRemove = prev.slice(needed);
            toRemove.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return prev.slice(0, needed);
        });
    }, [changeRequestDescriptionHtml, respondModalOpen, respondResponseTab]);

    const openChangeRequestDraft = useCallback(
        (sourceRequest) => {
            setSelectedChangeRequestSource(sourceRequest ?? null);
            changeRequestForm.setFieldsValue({
                description: '',
            });
            setChangeRequestPendingLinks([]);
            setChangeRequestPendingFiles((prev) => {
                prev.forEach((p) => {
                    if (p?.preview) {
                        try {
                            URL.revokeObjectURL(p.preview);
                        } catch {
                            // ignore
                        }
                    }
                });
                return [];
            });
            setChangeRequestLinkModalOpen(false);
            changeRequestLinkModalCallbackRef.current = null;
        },
        [changeRequestForm],
    );

    const closeChangeRequestDraft = useCallback(() => {
        setSelectedChangeRequestSource(null);
        changeRequestForm.resetFields();
        setChangeRequestPendingLinks([]);
        setChangeRequestPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setChangeRequestLinkModalOpen(false);
        changeRequestLinkModalCallbackRef.current = null;
    }, [changeRequestForm]);

    const openRespondModal = useCallback(
        (requestId, action) => {
            setSelectedRespondRequestId(requestId);
            setRespondAction(action);
            setRespondResponseTab('backlog');
            setRespondNote('');
            setRespondRating(5);
            setRespondPendingLinks([]);
            setRespondPendingFiles([]);

            if (String(action || '').toLowerCase() === 'declined') {
                const source =
                    clientSubmissions.find((x) => String(x?.id ?? '') === String(requestId ?? '')) ?? null;
                openChangeRequestDraft(source);
            } else {
                closeChangeRequestDraft();
            }

            setRespondModalOpen(true);
        },
        [clientSubmissions, openChangeRequestDraft, closeChangeRequestDraft],
    );

    const closeRespondModal = useCallback(() => {
        setRespondModalOpen(false);
        setSelectedRespondRequestId(null);
        setRespondNote('');
        setRespondRating(5);
        setRespondPendingLinks([]);
        setRespondPendingFiles([]);
        setRespondLinkModalOpen(false);
        respondLinkModalCallbackRef.current = null;
        setRespondResponseTab('backlog');
        closeChangeRequestDraft();
    }, [closeChangeRequestDraft]);

    const handleChangeRequestFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImage = (file.type || '').startsWith('image/');
            return {
                id: `cr-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImage ? URL.createObjectURL(file) : null,
            };
        });
        setChangeRequestPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const handleChangeRequestRemoveAttachment = useCallback((id) => {
        setChangeRequestPendingFiles((prev) => {
            const removedIndex = prev.findIndex((p) => p.id === id);
            const match = prev.find((p) => p.id === id);
            if (match?.preview) {
                try {
                    URL.revokeObjectURL(match.preview);
                } catch {
                    // ignore
                }
            }
            if (removedIndex >= 0) {
                const currentHtml = changeRequestForm.getFieldValue('description') ?? '';
                const nextHtml = removeAttachmentPlaceholderFromHtml(currentHtml, removedIndex);
                changeRequestForm.setFieldValue('description', nextHtml);
            }
            return prev.filter((p) => p.id !== id);
        });
    }, []);

    const handleChangeRequestRemoveLink = useCallback((id) => {
        setChangeRequestPendingLinks((prev) => {
            const match = prev.find((l) => l.id === id);
            if (match?.url) {
                const currentHtml = changeRequestForm.getFieldValue('description') ?? '';
                const nextHtml = removeLinkFromHtml(currentHtml, match.url);
                changeRequestForm.setFieldValue('description', nextHtml);
            }
            return prev.filter((l) => l.id !== id);
        });
    }, [changeRequestForm]);

    const handleChangeRequestEditorLinkRemoved = useCallback((href) => {
        if (!href || typeof href !== 'string') return;
        if (href.startsWith('#')) return;
        setChangeRequestPendingLinks((prev) => prev.filter((l) => l.url !== href));
    }, []);

    const handleChangeRequestLinkRequest = useCallback(
        (insertLink) => {
            changeRequestLinkModalCallbackRef.current = insertLink;
            changeRequestLinkForm.resetFields();
            setChangeRequestLinkModalOpen(true);
        },
        [changeRequestLinkForm],
    );

    const closeChangeRequestLinkModal = useCallback(() => {
        setChangeRequestLinkModalOpen(false);
        changeRequestLinkModalCallbackRef.current = null;
    }, []);

    const handleChangeRequestLinkModalSubmit = useCallback(() => {
        changeRequestLinkForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) return;
            const insertLink = changeRequestLinkModalCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setChangeRequestPendingLinks((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setChangeRequestLinkModalOpen(false);
            changeRequestLinkModalCallbackRef.current = null;
        });
    }, [changeRequestLinkForm]);

    const submitChangeRequest = useCallback(
        (callbacks = {}) => {
            changeRequestForm.validateFields().then((values) => {
                // AntD form values can occasionally be stale if a controlled input hasn't flushed yet.
                // Prefer reading the current field value from the form instance.
                const rawDesc = String(changeRequestForm.getFieldValue('description') ?? values?.description ?? '').trim();
                let description = rawDesc;
                if (description) {
                    try {
                        // RichTextEditor can produce HTML that doesn't start with "<" (e.g. "Text<div>...")
                        // Strip attachment placeholders (files are sent separately) before converting to markdown.
                        const hasHtml = /<[^>]+>/.test(description);
                        if (hasHtml) {
                            const withoutAttachmentAnchors = description
                                .replace(/<a[^>]*href="#attachment-\d+"[^>]*>[\s\S]*?<\/a>/gi, '')
                                .replace(/\u200b/g, '');
                            description = htmlToMarkdown(withoutAttachmentAnchors).trim();
                        }
                    } catch {
                        // keep as-is
                    }
                }
                const attachmentUrls =
                    changeRequestPendingLinks.length > 0
                        ? changeRequestPendingLinks.map((l) => ({ type: 'link', url: l.url, title: l.title }))
                        : undefined;
                const files =
                    changeRequestPendingFiles.length > 0 ? changeRequestPendingFiles.map((p) => p.file) : undefined;
                createChangeRequestMutation.mutate(
                    {
                        description: description ?? '',
                        // Source is a completion request. Backend expects the underlying submission UUID.
                        submissionId:
                            selectedChangeRequestSource?.submissionId ??
                            selectedChangeRequestSource?.submission?.id ??
                            selectedChangeRequestSource?.submission?.submissionId ??
                            selectedChangeRequestSource?.id,
                        // Also send the completion request id so backend can link the change request to it.
                        completionRequestId: selectedChangeRequestSource?.id,
                        attachmentUrls,
                        files,
                    },
                    {
                        onSuccess: () => {
                            closeChangeRequestDraft();
                            callbacks.onSuccess?.();
                        },
                        onError: callbacks.onError,
                    },
                );
            });
        },
        [
            changeRequestForm,
            createChangeRequestMutation,
            closeChangeRequestDraft,
            changeRequestPendingLinks,
            changeRequestPendingFiles,
            selectedChangeRequestSource,
        ],
    );

    const handleRespondFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImage = (file.type || '').startsWith('image/');
            return {
                id: `respond-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImage ? URL.createObjectURL(file) : null,
            };
        });
        setRespondPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const handleRespondRemoveAttachment = useCallback((id) => {
        setRespondPendingFiles((prev) => {
            const removedIndex = prev.findIndex((p) => p.id === id);
            const match = prev.find((p) => p.id === id);
            if (match?.preview) {
                try {
                    URL.revokeObjectURL(match.preview);
                } catch {
                    // ignore
                }
            }
            if (removedIndex >= 0) {
                setRespondNote((cur) => removeAttachmentPlaceholderFromHtml(cur || '', removedIndex));
            }
            return prev.filter((p) => p.id !== id);
        });
    }, []);

    const handleRespondRemoveLink = useCallback((id) => {
        setRespondPendingLinks((prev) => {
            const match = prev.find((l) => l.id === id);
            if (match?.url) {
                setRespondNote((cur) => removeLinkFromHtml(cur || '', match.url));
            }
            return prev.filter((l) => l.id !== id);
        });
    }, []);

    const handleRespondEditorLinkRemoved = useCallback((href) => {
        if (!href || typeof href !== 'string') return;
        if (href.startsWith('#')) return;
        setRespondPendingLinks((prev) => prev.filter((l) => l.url !== href));
    }, []);

    const handleRespondLinkRequest = useCallback(
        (insertLink) => {
            respondLinkModalCallbackRef.current = insertLink;
            respondLinkForm.resetFields();
            setRespondLinkModalOpen(true);
        },
        [respondLinkForm],
    );

    const closeRespondLinkModal = useCallback(() => {
        setRespondLinkModalOpen(false);
        respondLinkModalCallbackRef.current = null;
    }, []);

    const handleRespondLinkModalSubmit = useCallback(() => {
        respondLinkForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) return;
            const insertLink = respondLinkModalCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setRespondPendingLinks((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setRespondLinkModalOpen(false);
            respondLinkModalCallbackRef.current = null;
        });
    }, [respondLinkForm]);

    const handleRespondSubmit = useCallback(
        (callbacks = {}) => {
            if (!selectedRespondRequestId) return;
            const note = (respondNote || '').trim() || undefined;
            const sanitizedRating =
                typeof respondRating === 'number' && !Number.isNaN(respondRating)
                    ? Math.max(1, Math.min(5, Math.round(respondRating)))
                    : undefined;
            const attachmentUrls =
                respondPendingLinks.length > 0
                    ? respondPendingLinks.map((l) => ({ type: 'link', url: l.url, title: l.title }))
                    : undefined;
            const files =
                respondPendingFiles.length > 0 ? respondPendingFiles.map((p) => p.file) : undefined;
            respondMutation.mutate(
                {
                    requestId: selectedRespondRequestId,
                    action: respondAction,
                    note,
                    rating: respondAction === 'accepted' ? sanitizedRating : undefined,
                    attachmentUrls,
                    files,
                },
                {
                    onSuccess: () => {
                        closeRespondModal();
                        callbacks.onSuccess?.();
                    },
                    onError: callbacks.onError,
                },
            );
        },
        [
            selectedRespondRequestId,
            respondAction,
            respondNote,
            respondRating,
            respondPendingLinks,
            respondPendingFiles,
            respondMutation,
            closeRespondModal,
        ],
    );

    const [selectedTask, setSelectedTask] = useState(null);
    const [pmoTaskDetailBugs, setPmoTaskDetailBugs] = useState([]);

    const taskDescriptionMarkdown = useMemo(() => {
        const task = selectedTask?.task;
        return task ? getTaskDescriptionMarkdown(task) : '';
    }, [selectedTask?.task]);

    const hydrateTaskDetail = useCallback(async (sprint, task) => {
        if (!projectId || !sprint?.id || !task) return;
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        if (!taskId) return;
        try {
            const detail = await queryClient.fetchQuery({
                queryKey: PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY(projectId, sprint.id, taskId),
                queryFn: () => getProjectSprintTaskDetail(projectId, sprint.id, taskId),
                staleTime: 60 * 1000,
            });
            const fullTask =
                detail?.task && typeof detail.task === 'object'
                    ? detail.task
                    : detail && typeof detail === 'object'
                        ? detail
                        : task;
            const responseBugs = Array.isArray(detail?.bugs)
                ? detail.bugs
                : Array.isArray(fullTask?.bugs)
                    ? fullTask.bugs
                    : [];
            const fromApi = projectBugs.filter((b) => String(b?.task?.id ?? b?.taskId) === String(taskId));
            setSelectedTask((prev) => {
                if (!prev) return prev;
                if (String(prev?.sprint?.id ?? '') !== String(sprint.id)) return prev;
                const prevTaskId = getSprintTaskApiId(prev?.task) ?? prev?.task?.id ?? prev?.task?.taskId;
                if (String(prevTaskId ?? '') !== String(taskId)) return prev;
                return { sprint, task: fullTask };
            });
            setPmoTaskDetailBugs([...responseBugs, ...fromApi]);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Failed to load task details');
        }
    }, [projectId, queryClient, projectBugs]);

    const handleTaskSelect = useCallback((sprint, task) => {
        setSelectedTask({ sprint, task });
        const fromTask = Array.isArray(task?.bugs) ? task.bugs : [];
        const taskId = getSprintTaskApiId(task) ?? task?.id ?? task?.taskId;
        const fromApi = taskId == null ? [] : projectBugs.filter((b) => String(b?.task?.id ?? b?.taskId) === String(taskId));
        setPmoTaskDetailBugs([...fromTask, ...fromApi]);
        void hydrateTaskDetail(sprint, task);
    }, [projectBugs, hydrateTaskDetail]);

    /** Click submission header / card chrome → submit-to-client modal (description + task list). */
    const handleSubmissionKanbanOpen = useCallback(
        (submission) => {
            if (submission) openSubmitModal(submission);
        },
        [openSubmitModal],
    );

    /** Click nested task → task detail only (not sprint/milestone). */
    const handleTaskFromSubmissionKanban = useCallback(
        (submission, entry) => {
            if (!entry) return;
            if (entry.isSynthetic) {
                openSubmitModal(submission);
                return;
            }
            handleTaskSelect(entry.sprint, entry.task);
        },
        [openSubmitModal, handleTaskSelect],
    );

    /** Submitted-to-client column: never open submit modal; task rows open view-only detail. */
    const handleTaskFromSubmittedClientKanban = useCallback(
        (_submission, entry) => {
            if (!entry) return;
            handleTaskSelect(entry.sprint, entry.task);
        },
        [handleTaskSelect],
    );

    /** Submitted column header → read-only submission detail (notes, links, tasks). */
    const handleSubmittedClientKanbanOpen = useCallback(
        (submission) => {
            if (submission) openSubmissionDetailModal(submission);
        },
        [openSubmissionDetailModal],
    );

    const closeTaskPanel = useCallback(() => {
        setSelectedTask(null);
        setPmoTaskDetailBugs([]);
    }, []);

    const [dropTarget, setDropTarget] = useState(null);
    const [draggingColumn, setDraggingColumn] = useState(null);

    const clearDropTarget = useCallback(() => {
        setDropTarget(null);
        setDraggingColumn(null);
    }, []);

    const handleTaskMove = useCallback(
        ({ sprintId, taskId, sourceColumn, targetColumn, submissionId }) => {
            clearDropTarget();
            if (!sourceColumn || !targetColumn) return;
            if (sourceColumn === targetColumn) return;

            if (sourceColumn === COL_PMO.SUBMISSION && targetColumn === COL_PMO.SUBMITTED_CLIENT) {
                if (!canSubmitToClient) {
                    message.warning("You don't have permission to perform this action.");
                    return;
                }
                let sub =
                    submissionId != null
                        ? submissions.find((s) => String(s.id) === String(submissionId))
                        : null;
                if (!sub) {
                    sub = findSubmissionForTask(submissions, sprintId, taskId);
                }
                if (sub && isSubmissionPending(sub)) {
                    openSubmitModal(sub);
                } else {
                    message.warning('Nothing to submit for this item.');
                }
                return;
            }
            message.warning('This move is not supported');
        },
        [clearDropTarget, submissions, openSubmitModal, canSubmitToClient],
    );

    const handleDndDragStart = useCallback((payload) => {
        setDraggingColumn(payload?.sourceColumn ?? null);
    }, []);

    const handleDndDragOverColumn = useCallback((columnId) => {
        setDropTarget(columnId);
    }, []);

    const handleDndDragEnd = useCallback(
        ({ overColumnId, payload }) => {
            const targetColumn = overColumnId ?? null;
            handleTaskMove({
                sprintId: payload?.sprintId,
                taskId: payload?.taskId,
                sourceColumn: payload?.sourceColumn,
                targetColumn,
                submissionId: payload?.submissionId,
            });
        },
        [handleTaskMove],
    );

    const isLoading = isClient ? completionLoading : pmoLoading;

    const total = isClient ? completionData?.total ?? 0 : pmoData?.total ?? 0;
    const totalPages = isClient ? completionData?.totalPages ?? 1 : pmoData?.totalPages ?? 1;

    return {
        submissions,
        clientSubmissions,
        total,
        page,
        limit,
        totalPages,
        isLoading,
        isError: pmoError,
        refetch: refetchPmo,
        refetchCompletion,
        setPage,
        setLimit,
        onPageChange: handlePageChange,
        isClientView: isClient,
        canSubmitToClient,
        canRespondToRequest,
        canCreateChangeRequest,
        canViewChangeRequests,
        pmoViewTab,
        setPmoViewTab,
        submissionColumn,
        submittedToClientColumn,
        submitToClient,
        submitToClientPending: submitToClientMutation.isPending,
        submitToClientPendingId: pendingSubmitToClientId,
        submitModalOpen,
        selectedSubmission,
        submissionDetailModalOpen,
        detailSubmission,
        notes,
        setNotes,
        submitModalPresentation,
        pmoSubmitModalTaskRows,
        submitModalSubmissionDescriptionRaw,
        submitModalDetailSections,
        submissionDetailPresentation,
        pmoSubmissionDetailTaskRows,
        submissionDetailDescriptionRaw,
        submissionDetailViewLinks,
        submissionDetailViewAttachments,
        submissionDetailViewClientAttachments,
        submissionDetailSections,
        pendingLinks,
        pendingFiles,
        openSubmitModal,
        closeSubmitModal,
        closeSubmissionDetailModal,
        handleFilesSelected,
        handleRemoveAttachment,
        handleRemoveLink,
        handleLinkRequest,
        linkModalOpen,
        linkForm,
        closeLinkModal,
        handleLinkModalSubmit,
        handleSubmitToClient,
        respondModalOpen,
        respondAction,
        respondResponseTab,
        setRespondResponseTab,
        respondNote,
        setRespondNote,
        respondRating,
        setRespondRating,
        respondPendingLinks,
        respondPendingFiles,
        openRespondModal,
        closeRespondModal,
        handleRespondFilesSelected,
        handleRespondRemoveAttachment,
        handleRespondRemoveLink,
        handleRespondEditorLinkRemoved,
        handleRespondLinkRequest,
        respondLinkModalOpen,
        respondLinkForm,
        closeRespondLinkModal,
        handleRespondLinkModalSubmit,
        handleRespondSubmit,
        respondPending: respondMutation.isPending,
        changeRequestForm,
        selectedChangeRequestSource,
        openChangeRequestDraft,
        closeChangeRequestDraft,
        submitChangeRequest,
        createChangeRequestPending: createChangeRequestMutation.isPending,
        changeRequestsLoading,
        clientFeedbackItems,
        changeRequestPendingLinks,
        changeRequestPendingFiles,
        handleChangeRequestFilesSelected,
        handleChangeRequestRemoveAttachment,
        handleChangeRequestRemoveLink,
        handleChangeRequestEditorLinkRemoved,
        changeRequestLinkModalOpen,
        changeRequestLinkForm,
        closeChangeRequestLinkModal,
        handleChangeRequestLinkModalSubmit,
        handleChangeRequestLinkRequest,
        selectedTask,
        taskDescriptionMarkdown,
        pmoTaskDetailBugs,
        handleTaskSelect,
        handleSubmissionKanbanOpen,
        handleTaskFromSubmissionKanban,
        handleTaskFromSubmittedClientKanban,
        handleSubmittedClientKanbanOpen,
        closeTaskPanel,
        clientRequestDetailOpen,
        clientRequestDetail,
        openClientRequestDetail,
        closeClientRequestDetail,
        clientRequestDetailPresentation,
        clientRequestDetailNotesRaw,
        clientRequestDetailViewAttachments,
        clientRequestDetailViewClientAttachments,
        clientRequestDetailSections,
        dropTarget,
        draggingColumn,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        clearDropTarget,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
    };
}
